/**
 * Cart drawer — Task 35.1
 */
import { useTranslation } from 'react-i18next'
import { X, Minus, Plus, Trash2, AlertCircle, Trash } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useFeatures } from '@/hooks/useFeatures'
import { useAuthStore } from '@/stores/authStore'
import { useCartStore } from '@/stores/cartStore'
import { useCart, useUpdateCartItem, useRemoveCartItem } from '@/hooks/useCart'
import { useUIStore } from '@/stores/uiStore'
import { useStoreLogo } from '@/hooks/useStoreLogo'
import { formatCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { CouponInput } from './CouponInput'
import { ProductThumbnail } from './ProductThumbnail'

export function CartDrawer() {
  const { t } = useTranslation('cart')
  const { isOpen, closeCart } = useCartStore()
  const { currency } = useUIStore()
  const { features } = useFeatures()
  const { isAuthenticated } = useAuthStore()
  const canCheckout = isAuthenticated || features.enable_guest_checkout
  const storeLogoSrc = useStoreLogo()
  const { data: cart, isLoading } = useCart()
  const updateItem = useUpdateCartItem()
  const removeItem = useRemoveCartItem()

  const handleClearCart = async () => {
    if (!cart?.items.length) return
    if (!window.confirm('Are you sure you want to clear all items from your cart?')) return
    
    // Remove all items
    for (const item of cart.items) {
      await removeItem.mutateAsync(item.id)
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm animate-fade-in"
        onClick={closeCart}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-white/95 backdrop-blur-xl shadow-[0_0_50px_rgba(0,0,0,0.15)] rounded-l-3xl border-l border-gray-100/50 animate-slide-in-right"
        role="dialog"
        aria-label={t('your_cart')}
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
          <h2 className="font-heading text-xl font-bold text-gray-900">
            {t('your_cart')}
            {cart && cart.items.length > 0 && (
              <span className="ml-2 text-sm font-medium text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full">
                {t('item_count', { count: cart.items.length })}
              </span>
            )}
          </h2>
          <button
            onClick={closeCart}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            aria-label="Close cart"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 scrollbar-thin">
          {isLoading ? (
            <div className="flex h-32 items-center justify-center text-gray-400">
              <span className="animate-pulse font-medium">Loading your items…</span>
            </div>
          ) : !cart || cart.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-5 py-24 text-center">
              <div className="h-16 w-16 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center">
                <Minus className="h-8 w-8" />
              </div>
              <div>
                <p className="text-lg font-bold font-heading text-gray-800">{t('empty_cart')}</p>
                <p className="text-sm text-gray-400 mt-1">Add items to get started!</p>
              </div>
              <Button variant="outline" onClick={closeCart} asChild className="rounded-xl px-6">
                <Link to="/products">{t('empty_cart_cta')}</Link>
              </Button>
            </div>
          ) : (
            <ul className="space-y-6">
              {cart.items.map((item) => (
                <li key={item.id} className="flex gap-4 p-3 rounded-2xl border border-gray-50 bg-white shadow-sm hover:border-primary-100 transition-all duration-300">
                  {/* Product image */}
                  <ProductThumbnail
                    product={item.product}
                    fallbackSrc={storeLogoSrc}
                    alt={item.product?.name}
                    className="h-20 w-20 flex-shrink-0 rounded-xl border border-gray-100"
                  />

                  {/* Details */}
                  <div className="flex flex-1 flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start gap-2">
                        <Link
                          to={`/products/${item.product?.slug ?? ''}`}
                          className="font-heading text-sm font-semibold text-gray-800 hover:text-primary-600 line-clamp-1 transition-colors"
                          onClick={closeCart}
                        >
                          {item.product?.name ?? 'Unknown product'}
                        </Link>
                        <button
                          onClick={() => removeItem.mutate(item.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors p-1"
                          aria-label="Remove item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {item.variant && (
                        <p className="mt-1 text-xs text-gray-400">
                          {Object.entries(item.variant.attributes)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(', ')}
                        </p>
                      )}
                    </div>

                    {/* Price changed notice */}
                    {item.price_changed && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                        <AlertCircle className="h-3 w-3" />
                        {t('price_changed')}
                      </div>
                    )}

                    <div className="mt-3 flex items-center justify-between">
                      {/* Quantity controls */}
                      <div className="flex items-center rounded-xl border border-gray-200 bg-gray-50/50">
                        <button
                          onClick={() =>
                            updateItem.mutate({ itemId: item.id, quantity: item.quantity - 1 })
                          }
                          disabled={item.quantity <= 1}
                          className="flex h-8 w-8 items-center justify-center text-gray-500 hover:bg-gray-100 rounded-l-xl disabled:opacity-40"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="w-8 text-center text-xs font-semibold text-gray-700">{item.quantity}</span>
                        <button
                          onClick={() =>
                            updateItem.mutate({ itemId: item.id, quantity: item.quantity + 1 })
                          }
                          className="flex h-8 w-8 items-center justify-center text-gray-500 hover:bg-gray-100 rounded-r-xl"
                          aria-label="Increase quantity"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>

                      <span className="text-sm font-bold font-heading text-gray-800">
                        {formatCurrency(item.subtotal, currency)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer — totals + checkout */}
        {cart && cart.items.length > 0 && (
          <div className="border-t border-gray-100 px-6 py-6 space-y-4 bg-gray-50/50 rounded-b-3xl">
            <CouponInput />

            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-500">
                <span>Subtotal</span>
                <span className="font-semibold text-gray-700">{formatCurrency(cart.subtotal, currency)}</span>
              </div>
              {cart.coupon_discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Coupon ({cart.coupon_code})</span>
                  <span className="font-semibold">-{formatCurrency(cart.coupon_discount, currency)}</span>
                </div>
              )}
              {cart.loyalty_discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Loyalty discount</span>
                  <span className="font-semibold">-{formatCurrency(cart.loyalty_discount, currency)}</span>
                </div>
              )}
              {cart.applied_promotions.map((promo) => (
                <div key={promo.id} className="flex justify-between text-green-600">
                  <span>{promo.name}</span>
                  <span className="font-semibold">-{formatCurrency(promo.discount_amount, currency)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-gray-100 pt-3 text-base font-bold text-gray-900 font-heading">
                <span>Total</span>
                <span className="text-primary-700">{formatCurrency(cart.grand_total, currency)}</span>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <Button className="w-full h-12 rounded-xl font-semibold shadow-sm bg-primary-600 hover:bg-primary-700 hover:scale-[1.02] transition-all" asChild onClick={closeCart}>
                <Link
                  to={canCheckout ? '/checkout' : '/auth/login'}
                  state={canCheckout ? undefined : { from: '/checkout' }}
                >
                  {canCheckout ? 'Proceed to Checkout' : 'Sign in to Checkout'}
                </Link>
              </Button>
              <Button variant="outline" className="w-full h-12 rounded-xl font-semibold" onClick={closeCart} asChild>
                <Link to="/cart">View Cart</Link>
              </Button>
              <Button 
                variant="outline" 
                className="w-full h-10 rounded-xl font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300"
                onClick={handleClearCart}
              >
                <Trash className="h-4 w-4 mr-2" />
                Clear Cart
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
